import React, { useState, useEffect } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase.js';
import * as utils from '../../common/Utils.js';

const DraftInitPanel = ({ season, signups, teams, players, onSaved, initialCorePlayers = [], initialDraftOrder = [] }) => {
  const paidSignups = signups
    .filter(s => s.paid)
    .sort((a, b) => {
      const pa = utils.getPlayerByID(players, a.player_id);
      const pb = utils.getPlayerByID(players, b.player_id);
      const nameA = pa ? `${pa.lastName || ''} ${pa.firstName || ''}`.toLowerCase() : '';
      const nameB = pb ? `${pb.lastName || ''} ${pb.firstName || ''}`.toLowerCase() : '';
      return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
    });

  const [corePlayers, setCorePlayers] = useState(initialCorePlayers);
  const [corePlayersLocked, setCorePlayersLocked] = useState(true);
  const [unlockPrompt, setUnlockPrompt] = useState(false);
  const [draftOrder, setDraftOrder] = useState(
    initialDraftOrder.length > 0 ? initialDraftOrder : teams.map(t => t.id)
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraftOrder(prev =>
      prev.length > 0 ? prev : teams.map(t => t.id)
    );
  }, [teams]);

  const toggleCorePlayer = (playerID) => {
    setCorePlayers(prev =>
      prev.includes(playerID) ? prev.filter(id => id !== playerID) : [...prev, playerID]
    );
  };

  const moveTeam = (index, delta) => {
    const next = index + delta;
    if (next < 0 || next >= draftOrder.length) return;
    const updated = [...draftOrder];
    [updated[index], updated[next]] = [updated[next], updated[index]];
    setDraftOrder(updated);
  };

  const undraftedPlayerIDs = paidSignups
    .map(s => s.player_id)
    .filter(id => !corePlayers.includes(id));

  const undraftedPlayers = undraftedPlayerIDs
    .map(id => utils.getPlayerByID(players, id))
    .filter(Boolean);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'draft', season), {
        corePlayers,
        draftOrder,
        draftedPlayers: new Array(undraftedPlayers.length).fill(''),
        undraftedPlayers,
      }, { merge: true });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="league-table-container schedule" style={{ maxWidth: 700 }}>
      <p className="league-title-standard-secondary text-align-center">
        Initialize Season {season} Draft
      </p>

      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 2 }}>
          <p className="league-title-standard-small" style={{ margin: 0 }}>
            Core Players
          </p>
          <button
            type="button"
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 18, lineHeight: 1 }}
            title={corePlayersLocked ? 'Click to unlock' : 'Click to lock'}
            onClick={() => { setCorePlayersLocked(v => !v); setUnlockPrompt(false); }}
          >
            {corePlayersLocked ? '🔒' : '🔓'}
          </button>
          {!corePlayersLocked && corePlayers.length > 0 && (
            <button
              className="btn btn-sm btn-outline-secondary"
              type="button"
              onClick={() => setCorePlayers([])}
            >
              Clear All
            </button>
          )}
        </div>
        <p style={{ color: '#666', fontSize: 14, marginBottom: 4 }}>
          Choose players who are already assigned to a team before the draft and are not part of the draft.
        </p>
        <p style={{ color: '#666', fontSize: 14, marginBottom: 10 }}>
          {paidSignups.length} paid signups — {corePlayers.length} core, {undraftedPlayers.length} draftable.{' '}
          <a href="/admin/signups">Mark a player as paid on the signups page</a> to add them to the season.
        </p>
        {unlockPrompt && (
          <p style={{ color: '#b45309', fontSize: 13, marginBottom: 8 }}>
            🔒 Unlock the list first to make changes.
          </p>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {paidSignups.map(signup => {
            const player = utils.getPlayerByID(players, signup.player_id);
            if (!player) return null;
            const isCore = corePlayers.includes(signup.player_id);
            return (
              <label
                key={signup.player_id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '5px 10px', border: '1px solid #ccc', borderRadius: 20,
                  cursor: corePlayersLocked ? 'default' : 'pointer', fontSize: 13,
                  background: isCore ? '#222' : '#fff',
                  color: isCore ? '#fff' : '#222',
                  opacity: corePlayersLocked ? 0.75 : 1,
                }}
                onClick={() => {
                  if (corePlayersLocked) {
                    setUnlockPrompt(true);
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={isCore}
                  onChange={() => { if (!corePlayersLocked) toggleCorePlayer(signup.player_id); }}
                  onClick={e => { if (corePlayersLocked) e.preventDefault(); }}
                  style={{ display: 'none' }}
                />
                {utils.displayPlayerName(player)}
              </label>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <p className="league-title-standard-small">Draft Order</p>
        <p style={{ color: '#666', fontSize: 14, marginBottom: 4 }}>
          Use arrows to reorder teams. Draft will snake after each round.
        </p>
        <p style={{ color: '#666', fontSize: 14, marginBottom: 10 }}>
          <a href={`/admin/seasons/${season}`}>Add or modify the teams in this season</a> before setting the draft order.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {draftOrder.map((teamID, index) => {
            const team = utils.getTeamForID(teams, teamID);
            if (!team) return null;
            return (
              <div
                key={teamID}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6,
                  background: '#fafafa',
                }}
              >
                <span style={{ minWidth: 24, color: '#888', fontSize: 13 }}>
                  {index + 1}.
                </span>
                <span style={{ flex: 1 }}>{team.name}</span>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={index === 0}
                  onClick={() => moveTeam(index, -1)}
                >
                  ▲
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={index === draftOrder.length - 1}
                  onClick={() => moveTeam(index, 1)}
                >
                  ▼
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: 16, padding: '12px 16px', background: '#f5f5f5', borderRadius: 6 }}>
        <strong>{undraftedPlayers.length}</strong> draftable players ·{' '}
        <strong>{draftOrder.length}</strong> teams ·{' '}
        Draft will create <strong>{undraftedPlayers.length}</strong> pick slots
      </div>

      <button
        className="btn btn-dark"
        disabled={saving || draftOrder.length === 0}
        onClick={handleSave}
      >
        {saving ? 'Saving…' : 'Save Draft'}
      </button>
    </div>
  );
};

export default DraftInitPanel;
